import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { invoicingApi } from './api'
import type { CreditNotePayload, CustomerPayload, InvoiceFilters, InvoicePayload, InvoicingSettingsPatch, RefundPayload, WriteOffPayload } from './types'
import { isPendingApproval } from './types'

// ---------- Recurring invoice templates ----------

export function useRecurringInvoiceTemplates() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'invoicing', 'recurring'),
    queryFn: () => invoicingApi.listRecurring(),
  })
}

export function useCreateRecurringInvoiceTemplate() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (body: import('./types').RecurringInvoiceTemplatePayload) =>
      invoicingApi.createRecurring(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'recurring') }),
  })
}

export function useUpdateRecurringInvoiceTemplate() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<import('./types').RecurringInvoiceTemplatePayload> }) =>
      invoicingApi.updateRecurring(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'recurring') }),
  })
}

export function useDeleteRecurringInvoiceTemplate() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: number) => invoicingApi.deleteRecurring(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'recurring') }),
  })
}

// ---------- AR stats (dashboard + platform admin) ----------

export function useArSummary(window?: { from: string; to: string }) {
  const tenantId = useTenant()
  // Why: when the dashboard period selector resolves to a window, key
  // the query on it so switching ranges re-fetches, and tenant-level
  // mutations (still keyed on 'ar-summary') invalidate every variant.
  const sig = window?.from && window?.to ? `${window.from}_${window.to}` : 'mtd'
  return useQuery({
    queryKey: tenantKey(tenantId, 'invoicing', 'ar-summary', sig),
    queryFn: () => invoicingApi.arSummary(window),
  })
}

// ---------- Tenant invoicing settings (Company page card) ----------

export function useInvoicingSettings() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'invoicing', 'settings'),
    queryFn: () => invoicingApi.getSettings(),
  })
}

export function useUpdateInvoicingSettings() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (patch: InvoicingSettingsPatch) => invoicingApi.patchSettings(patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'settings') })
      // The invoice editor's "next number" preview reads from the same
      // invoice_number_sequences row the settings card writes to.
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'invoices', 'next-number') })
    },
  })
}

// ---------- Customers ----------

export function useCustomers(params?: { search?: string; isActive?: boolean }) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'invoicing', 'customers', params || {}),
    queryFn: () => invoicingApi.listCustomers(params),
  })
}

export function useCustomer(id: number | undefined) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'invoicing', 'customer', id || 0),
    queryFn: () => invoicingApi.getCustomer(id as number),
    enabled: !!id,
  })
}

function invalidateCustomers(qc: ReturnType<typeof useQueryClient>, tenantId: string | null) {
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'customers') })
}

export function useCreateCustomer() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (body: CustomerPayload) => invoicingApi.createCustomer(body),
    onSuccess: () => invalidateCustomers(qc, tenantId),
  })
}

export function useUpdateCustomer() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: CustomerPayload }) =>
      invoicingApi.updateCustomer(id, body),
    onSuccess: () => {
      invalidateCustomers(qc, tenantId)
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'customer') })
    },
  })
}

export function useDeactivateCustomer() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: number) => invoicingApi.deactivateCustomer(id),
    onSuccess: (_res, id) => {
      invalidateCustomers(qc, tenantId)
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'customer', id) })
    },
  })
}

export function useReactivateCustomer() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: number) => invoicingApi.reactivateCustomer(id),
    onSuccess: (_res, id) => {
      invalidateCustomers(qc, tenantId)
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'customer', id) })
    },
  })
}

// ---------- Invoices ----------

export function useInvoices(filters?: InvoiceFilters) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'invoicing', 'invoices', filters || {}),
    queryFn: () => invoicingApi.listInvoices(filters),
  })
}

export function useInvoice(id: number | undefined) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'invoicing', 'invoice', id || 0),
    queryFn: () => invoicingApi.getInvoice(id as number),
    enabled: !!id,
  })
}

export function useNextInvoiceNumber() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'invoicing', 'invoices', 'next-number'),
    queryFn: () => invoicingApi.nextNumber(),
  })
}

function invalidateInvoices(qc: ReturnType<typeof useQueryClient>, tenantId: string | null, id?: number) {
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'invoices') })
  if (id !== undefined) {
    qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'invoice', id) })
  }
  // Dashboard AR widget + reporting read from the same accounting data.
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'ar-summary') })
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'dashboard') })
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'reporting') })
}

export function useCreateInvoice() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (body: InvoicePayload) => invoicingApi.createInvoice(body),
    onSuccess: (res) => invalidateInvoices(qc, tenantId, res.invoice.id),
  })
}

export function useUpdateInvoice() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<InvoicePayload> }) =>
      invoicingApi.updateInvoice(id, body),
    onSuccess: (res) => invalidateInvoices(qc, tenantId, res.invoice.id),
  })
}

export function usePostInvoice() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: number) => invoicingApi.postInvoice(id),
    onSuccess: (res, id) => {
      // Pending-approval 202 doesn't carry an invoice; invalidate by id
      // we already have, and nudge the approvals queries so the new
      // instance shows up in the inbox.
      invalidateInvoices(qc, tenantId, id)
      if (isPendingApproval(res)) {
        qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'approvals') })
      }
    },
  })
}

export function useSendInvoice() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: number) => invoicingApi.sendInvoice(id),
    onSuccess: (res) => invalidateInvoices(qc, tenantId, res.invoice.id),
  })
}

export function useVoidInvoice() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      invoicingApi.voidInvoice(id, reason),
    onSuccess: (res) => invalidateInvoices(qc, tenantId, res.invoice.id),
  })
}

export function useDeleteDraftInvoice() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: number) => invoicingApi.deleteDraftInvoice(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'invoices') })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'ar-summary') })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'dashboard') })
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'reporting') })
    },
  })
}

export function useArAging(asOf?: string) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'invoicing', 'ar-aging', asOf || 'today'),
    queryFn: () => invoicingApi.arAging(asOf ? { as_of: asOf } : undefined),
  })
}

export function useIssuePortalToken() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (customerId: number) => invoicingApi.issuePortalToken(customerId),
    onSuccess: (_r, customerId) => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'customer', customerId) })
    },
  })
}

export function useRevokePortalToken() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (customerId: number) => invoicingApi.revokePortalToken(customerId),
    onSuccess: (_r, customerId) => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'customer', customerId) })
    },
  })
}

export function useCreditNotes(params?: { invoice_id?: number; customer_id?: number }) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'invoicing', 'credit-notes', params || {}),
    queryFn: () => invoicingApi.listCreditNotes(params),
  })
}

function invalidateArAndApprovals(
  qc: ReturnType<typeof useQueryClient>,
  tenantId: string | null,
  invoiceId: number,
  res: unknown,
) {
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'invoice', invoiceId) })
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'invoices') })
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'credit-notes') })
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'ar-summary') })
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'reporting') })
  if (isPendingApproval(res)) {
    qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'approvals') })
  }
}

export function useIssueCreditNote() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (body: CreditNotePayload) => invoicingApi.issueCreditNote(body),
    onSuccess: (res, body) => invalidateArAndApprovals(qc, tenantId, body.invoice_id, res),
  })
}

export function useIssueRefund() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (body: RefundPayload) => invoicingApi.issueRefund(body),
    onSuccess: (res, body) => invalidateArAndApprovals(qc, tenantId, body.invoice_id, res),
  })
}

export function useWriteOffInvoice() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (body: WriteOffPayload) => invoicingApi.writeOffInvoice(body),
    onSuccess: (res, body) => invalidateArAndApprovals(qc, tenantId, body.invoice_id, res),
  })
}

export function useRemindInvoice() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: number) => invoicingApi.remind(id),
    onSuccess: (_res, id) => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'invoicing', 'invoice', id) })
    },
  })
}

export function useMarkInvoicePaid() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (args: {
      id: number
      amount: number
      received_at?: string
      method?: string
      note?: string
    }) => invoicingApi.markPaid(args.id, {
      amount: args.amount,
      received_at: args.received_at,
      method: args.method,
      note: args.note,
    }),
    onSuccess: (res) => invalidateInvoices(qc, tenantId, res.invoice.id),
  })
}

// ---------- Stripe Connect (Feature 2) ----------

export function useStripeConnectStatus() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'invoicing', 'stripe-connect', 'status'),
    queryFn: () => invoicingApi.getStripeConnectStatus(),
  })
}

export function useStripeConnectCountries(enabled: boolean) {
  return useQuery({
    queryKey: ['stripe-connect-countries'],
    queryFn: () => invoicingApi.listStripeConnectCountries(),
    enabled,
    staleTime: 60 * 60 * 1000,
  })
}

export function useSetStripeConnectMode() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (mode: 'connect_express' | 'bokapsys_managed') =>
      invoicingApi.setStripeConnectMode(mode),
    onSuccess: () => qc.invalidateQueries({
      queryKey: tenantKey(tenantId, 'invoicing', 'stripe-connect', 'status'),
    }),
  })
}

export function useStartStripeOnboarding() {
  return useMutation({
    mutationFn: (country: string) => invoicingApi.startStripeConnectOnboarding(country),
  })
}

export function useRefreshStripeOnboardingLink() {
  return useMutation({
    mutationFn: () => invoicingApi.refreshStripeConnectLink(),
  })
}

export function useSyncStripeConnect() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: () => invoicingApi.syncStripeConnect(),
    onSuccess: () => qc.invalidateQueries({
      queryKey: tenantKey(tenantId, 'invoicing', 'stripe-connect', 'status'),
    }),
  })
}

export function useOpenStripeDashboard() {
  return useMutation({
    mutationFn: () => invoicingApi.openStripeDashboard(),
  })
}

export function useDisconnectStripe() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: () => invoicingApi.disconnectStripe(),
    onSuccess: () => qc.invalidateQueries({
      queryKey: tenantKey(tenantId, 'invoicing', 'stripe-connect', 'status'),
    }),
  })
}
